import {Table} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {ElevationReviewItem} from "../../../../api/ElevationTool";
import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {OpenInNew} from "@mui/icons-material";
import {DrwOutsideSizeCol} from "../../shopdrawing/openings/DrwOutsideSizeCol";

// the quote-mode drw elevation review screens (door/screen/hardware) — the quote-opening
// equivalent of the shop-drawing ReviewDrwElevations. Scoped to the current alternative;
// opens the editor for one at its first linked quote opening in the matching workspace.
export type DrwElevationKind = "door" | "screen" | "hardware";

function fetchReview(kind: DrwElevationKind, project: number, alternative: number) {
    switch(kind) {
        case "door": return api.elevationTool.reviewDoorElevations({project, alternative});
        case "screen": return api.elevationTool.reviewScreenElevations({project, alternative});
        case "hardware": return api.elevationTool.reviewHardwareElevations({project, alternative});
    }
}

export function ReviewDrwElevations(props: {kind: DrwElevationKind}) {
    const project = useProjectId();
    const alternative = useAlternative();
    const nav = useNavigate();

    // shown when the user opens an elevation that no opening links (see openInEditor).
    const [noOpeningsWarning, setNoOpeningsWarning] = useState(false);

    // open the editor at the first quote opening that links this elevation. With no linked
    // opening there's no reference opening to load, so warn instead.
    const openInEditor = (row: ElevationReviewItem) => {
        const first = row.openings[0];
        if(!first) {
            setNoOpeningsWarning(true);
            return;
        }

        nav(`/project/${project}/quote/${alternative}/elevation-tool?quoteOpening=${first.id}&alternative=${alternative}&workspace=${props.kind}`);
    };

    return (
        <>
            <Table<ElevationReviewItem>
                name={"quote-drw-" + props.kind + "-elevations"}
                fetch={() => fetchReview(props.kind, project, alternative)}
                fetchDeps={[project, alternative, props.kind]}
                columns={[
                    {
                        name: "Name",
                        width: 250,
                        render: data => (
                            <div style={{display: "flex", alignItems: "center"}}>
                                <div style={{flex: 1}}>{data.name}{data.global && " (global)"}</div>
                                <Tooltip title="Open in editor">
                                    <IconButton
                                        size="small"
                                        style={{color: grey["500"], padding: 2}}
                                        onClick={e => {
                                            e.stopPropagation();
                                            openInEditor(data);
                                        }}
                                    >
                                        <OpenInNew style={{fontSize: "0.9rem"}} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ),
                    },
                    {
                        name: "Openings",
                        width: 120,
                        render: data => {
                            if(data.openings.length === 0) return "0";

                            return (
                                <Tooltip title={data.openings.map(o => o.name).join(", ")}>
                                    <span>{data.openings.length}</span>
                                </Tooltip>
                            );
                        },
                    },
                    props.kind === "screen" && DrwOutsideSizeCol(),
                ]}
            />

            <Dialog open={noOpeningsWarning} onClose={() => setNoOpeningsWarning(false)}>
                <DialogTitle>No openings linked</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This elevation is not linked to any opening. Link one or more openings to it in
                        the openings grid before you set it up in the editor.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoOpeningsWarning(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
